import React, { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import LeashLayout from 'leash/components/layout/LeashLayout';
import { PackagesHeader } from 'leash/components/packagesHeader/PackagesHeader';
import { PackagesTable } from 'leash/components/packagesTable/PackagesTable';
import { usePackagesContext } from 'leash/context/packages/PackagesContext';
import { LeashRoutes, PackageSearchCategory } from 'leash/utilities/const';
import {
  getPackageFacets,
  mapTravelPackages,
} from 'leash/utilities/packages/packages.utilities';
import { PackageSearchCategories } from 'leash/types';
import { useLayoutState } from 'shared/components/layout/useLayoutState';
import { apiPackage } from 'utilities/http/api/leash/apiPackage';
import { notify } from 'utilities/notifications/notify';
import { log } from 'utilities/log/log';
import { BreadcrumbItem } from 'types';

const breadcrumbs: BreadcrumbItem[] = [
  {
    key: 'main',
    label: 'Main',
    path: '/',
  },
  {
    key: 'leash',
    label: 'Leash',
  },
  {
    key: 'allPackages',
    label: 'All Packages',
  },
];

const searchBreadcrumbs: BreadcrumbItem[] = [
  {
    key: 'main',
    label: 'Main',
    path: '/',
  },
  {
    key: 'allPackages',
    label: 'All Packages',
    path: LeashRoutes.Packages,
  },
  { key: 'searchResults', label: 'Search results' },
];

const travelSearchCategories: PackageSearchCategories[] = [
  PackageSearchCategory.Airport,
  PackageSearchCategory.Transporter,
  PackageSearchCategory.Airline,
  PackageSearchCategory.GroundTransporter,
];

export interface PackagesRouteParams {
  category?: PackageSearchCategories;
  query?: string;
}

export const Packages = (): ReactElement => {
  const { state, dispatch } = usePackagesContext();
  const { loading, fetchPackages } = state;
  const { dispatch: dispatchLayout } = useLayoutState();
  const { category, query }: PackagesRouteParams = useParams();

  useEffect(() => {
    const isSearchMode = Boolean(category && query);
    dispatchLayout({
      type: 'SET_BREADCRUMBS',
      breadcrumbs: isSearchMode ? searchBreadcrumbs : breadcrumbs,
    });
  }, [category, query]);

  useEffect(() => {
    const fetch = async () => {
      dispatch({ type: 'LOAD_PACKAGES' });
      try {
        const res = await apiPackage.getPackages();
        dispatch({
          type: 'SET_PACKAGES',
          payload: {
            facets: getPackageFacets(res?.json.travelPlans),
            packages: mapTravelPackages(res?.json.travelPlans),
          },
        });

        if (!res.isOk) {
          notify.error('Could not load any packages.');
        }
      } catch (e) {
        log.debug(e);
        notify.error('Could not load any packages.');
      }
    };

    fetchPackages && fetch();
  }, [fetchPackages]);

  useEffect(() => {
    if (!query || !category || loading) return;

    if (travelSearchCategories.includes(category)) {
      dispatch({ type: 'SEARCH_TRAVEL_PROPOSAL_FIELDS', category, query });
    } else {
      dispatch({ type: 'SEARCH_PACKAGES', category, query });
    }
  }, [loading]);

  return (
    <LeashLayout>
      <PackagesHeader />
      <PackagesTable
        dispatch={dispatch}
        packagesState={state}
        activeFooterTools={{ exportCSV: true }}
      />
    </LeashLayout>
  );
};
